import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { FaMapMarkerAlt, FaEnvelope, FaPhone, FaClock, FaStar, FaShieldAlt, FaCheck, FaUserCheck } from 'react-icons/fa';
import styled from 'styled-components';

interface Review {
  id: string;
  rating: number;
  comment: string;
  customerName?: string;
  userName?: string;
  userPhoto?: string | null;
  date?: string;
  createdAt?: string;
  verified?: boolean;
}

interface Company {
  commercialName: string;
  logo: string | null;
  logoBase64: string;
  address: string;
  commercialEmail: string;
  commercialPhone: string;
  workingHours?: string;
  userId?: string;
  coverImage?: string;
}

interface SocialProfile {
  bio: string;
  coverImage: string;
  displayName: string;
}

// Estilos com styled-components
const Container = styled.div`
  padding: 0;
  margin: 0 auto;
  background-color: #f8f9fa;
  min-height: 100vh;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.header`
  position: relative;
  height: 300px;
  overflow: hidden;
`;

const HeaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.5));
  z-index: 1;
`;

const CoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 0;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const ProfileSection = styled.section`
  max-width: 1200px;
  margin: -75px auto 0;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 2rem;
`;

const CompanyLogo = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #fff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  background-color: #fff;
`;

const CompanyName = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 1rem 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const VerifiedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #00a67e;
  font-weight: 500;
  padding: 0.5rem 1rem;
  background-color: rgba(0, 166, 126, 0.1);
  border-radius: 30px;
  margin: 0.5rem 0;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const BioSection = styled.div`
  margin: 1.5rem auto;
  max-width: 800px;
  text-align: center;
`;

const CompanyBio = styled.p`
  color: #4a4a4a;
  line-height: 1.8;
  font-size: 1.1rem;
  margin: 0;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  margin: 2rem auto;
  max-width: 800px;
  padding: 0 1rem;
  text-align: center;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #4a4a4a;
  font-size: 1rem;

  svg {
    width: 20px;
    height: 20px;
    color: #00a67e;
    flex-shrink: 0;
  }

  a {
    color: #00a67e;
    text-decoration: none;
    transition: color 0.2s;
  }

  a:hover {
    text-decoration: underline;
    color: #008c69;
  }
`;

const TrustSection = styled.section`
  padding: 0 1rem;
  margin: 1.5rem auto;
  max-width: 800px;
`;

const TrustIndicators = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 2rem;
  padding: 2rem;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const TrustItem = styled.div`
  text-align: center;
  padding: 1rem;
  flex: 1;
  min-width: 150px;
`;

const TrustNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #00a67e;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    margin-right: 0.5rem;
  }
`;

const TrustLabel = styled.div`
  color: #666;
  font-size: 1rem;
  font-weight: 500;
`;

const RatingSection = styled.section`
  padding: 0 2rem;
  margin: 1.5rem auto;
  max-width: 800px;
`;

const RatingOverview = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1.5rem;
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const AverageRatingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  border-right: 1px solid #eee;

  @media (max-width: 768px) {
    border-right: none;
    border-bottom: 1px solid #eee;
    padding-bottom: 2rem;
  }
`;

const AverageRatingValue = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: #00a67e;
  line-height: 1;
  margin-bottom: 0.5rem;
`;

const Stars = styled.div`
  display: flex;
  gap: 0.25rem;
  justify-content: center;
  font-size: 1.5rem;
  margin: 0.5rem 0;
`;

const Star = styled.span`
  color: #e4e5e9;
`;

const StarFilled = styled.span`
  color: #ffd700;
`;

const RatingCount = styled.div`
  font-size: 1rem;
  color: #666;
  margin-top: 0.5rem;
`;

const RatingDistribution = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
`;

const RatingBar = styled.div`
  display: grid;
  grid-template-columns: 50px 1fr 50px;
  align-items: center;
  gap: 1rem;
`;

const RatingStars = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #666;
  font-weight: 500;

  svg {
    color: #ffd700;
    margin-left: 0.25rem;
  }
`;

const RatingBarContainer = styled.div`
  height: 10px;
  background-color: #f0f0f0;
  border-radius: 5px;
  overflow: hidden;
  flex-grow: 1;
`;

const RatingBarFill = styled.div`
  height: 100%;
  background-color: #00a67e;
  border-radius: 5px;
`;

const RatingBarCount = styled.div`
  color: #666;
  font-weight: 500;
  text-align: right;
`;

const ReviewsSection = styled.section`
  padding: 0 1rem;
  margin: 1rem auto;
  max-width: 600px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 1rem;
  text-align: center;
`;

const NoReviews = styled.div`
  text-align: center;
  padding: 3rem;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  color: #666;
  font-size: 1.1rem;
`;

const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ReviewCard = styled.div`
  background: #fff;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ReviewerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ReviewerPhotoAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const ReviewerAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #00a67e;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
`;

const CustomerName = styled.span`
  font-weight: 600;
  color: #1a1a1a;
  font-size: 1.1rem;
`;

const ReviewDate = styled.span`
  color: #666;
  font-size: 0.9rem;
`;

const ReviewRating = styled.div`
  display: flex;
  gap: 0.25rem;
  margin: 0.75rem 0;

  svg {
    color: #e4e5e9;
    font-size: 1.25rem;
  }

  svg.filled {
    color: #ffd700;
  }
`;

const ReviewComment = styled.p`
  color: #4a4a4a;
  line-height: 1.6;
  margin: 1rem 0;
  font-size: 1.05rem;
`;

const ReviewFooter = styled.div`
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-start;
`;

const VerifiedPurchase = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #00a67e;
  font-size: 0.9rem;
  font-weight: 500;

  svg {
    font-size: 0.9rem;
  }
`;

const Footer = styled.footer`
  text-align: center;
  padding: 3rem 2rem;
  background: #fff;
  margin-top: 3rem;
  border-top: 1px solid #eee;
`;

const FooterContent = styled.div`
  max-width: 800px;
  margin: 0 auto;

  p {
    color: #666;
    margin-bottom: 1rem;
  }
`;

const FooterLogoLink = styled.a`
  display: inline-block;
  margin: 1rem 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const FooterLogo = styled.img`
  height: 30px;
`;

const FooterCopyright = styled.p`
  font-size: 0.9rem;
  color: #999;
  margin-top: 1rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  text-align: center;
  font-size: 1.2rem;
  color: #666;
  padding: 2rem;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  margin-bottom: 1rem;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #00a67e;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  text-align: center;
  font-size: 1.2rem;
  color: #d32f2f;
  padding: 2rem;
`;

const BackLink = styled.a`
  display: inline-block;
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background-color: #00a67e;
  color: white;
  text-decoration: none;
  border-radius: 30px;
  font-weight: 500;
  transition: background-color 0.2s;

  &:hover {
    background-color: #008c69;
  }
`;

export default function SellerReviews() {
  const { sellerId } = useParams();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [socialProfile, setSocialProfile] = useState<SocialProfile | null>(null);
  const [averageRating, setAverageRating] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      if (!sellerId) return;

      try {
        // Buscar dados da empresa
        const companyDoc = await getDoc(doc(db, 'companies', sellerId));
        
        if (companyDoc.exists()) {
          const companyData = companyDoc.data() as Company;
          console.log('Dados da empresa:', companyData);
          setCompany(companyData);

          // Buscar perfil social usando o userId da empresa
          if (companyData.userId) {
            const socialDoc = await getDoc(doc(db, 'socialProfiles', companyData.userId));
            if (socialDoc.exists()) {
              const socialData = socialDoc.data() as SocialProfile;
              console.log('Dados do perfil social:', socialData);
              setSocialProfile(socialData);
            }
          } else {
            // Se não houver userId, tentar buscar usando o próprio sellerId
            const socialDoc = await getDoc(doc(db, 'socialProfiles', sellerId));
            if (socialDoc.exists()) {
              const socialData = socialDoc.data() as SocialProfile;
              console.log('Dados do perfil social:', socialData);
              setSocialProfile(socialData);
            }
          }
        } else {
          setError('Empresa não encontrada');
          return;
        }

        // Buscar reviews
        const reviewsRef = collection(db, 'reviews');
        const reviewsQuery = query(reviewsRef, where('sellerId', '==', sellerId));
        const reviewsSnapshot = await getDocs(reviewsQuery);

        const reviewsData = reviewsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Review[];

        console.log('Reviews data:', reviewsData);
        setReviews(reviewsData);

        // Calcular média das avaliações
        if (reviewsData.length > 0) {
          const total = reviewsData.reduce((acc, review) => acc + review.rating, 0);
          setAverageRating(total / reviewsData.length);
        }
      } catch (err) {
        setError('Erro ao carregar os dados');
        console.error('Erro:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [sellerId]);

  useEffect(() => {
    document.title = company 
      ? `Avaliações de ${company.commercialName} - LOGNOW: A Sua Solução All-in-One para E-commerce`
      : 'Avaliações - LOGNOW';
  }, [company]);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingSpinner />
          <p>A carregar informações...</p>
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorContainer>
          <p>{error}</p>
          <BackLink href="https://lognow.pt">Voltar para LogNow</BackLink>
        </ErrorContainer>
      </Container>
    );
  }

  if (!company) {
    return (
      <Container>
        <ErrorContainer>
          <p>Empresa não encontrada</p>
          <BackLink href="https://lognow.pt">Voltar para LogNow</BackLink>
        </ErrorContainer>
      </Container>
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Data não disponível';
    
    try {
      // Se a data vier como timestamp do Firestore
      if (typeof dateString === 'object' && 'seconds' in dateString) {
        const timestamp = (dateString as any).seconds * 1000;
        return new Intl.DateTimeFormat('pt-PT', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }).format(new Date(timestamp));
      }
      
      // Se a data vier como string ISO
      if (typeof dateString === 'string') {
        return new Intl.DateTimeFormat('pt-PT', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }).format(new Date(dateString));
      }

      return 'Data não disponível';
    } catch (error) {
      console.error('Erro ao formatar data:', error, dateString);
      return 'Data não disponível';
    }
  };

  // Distribuição das avaliações por número de estrelas
  const ratingDistribution = [0, 0, 0, 0, 0]; // índice 0 = 1 estrela, índice 4 = 5 estrelas
  reviews.forEach(review => {
    if (review.rating >= 1 && review.rating <= 5) {
      ratingDistribution[review.rating - 1]++;
    }
  });

  return (
    <Container>
      <Header>
        <HeaderOverlay />
        <CoverImage 
          src={socialProfile?.coverImage || company?.coverImage || 'https://lognow.pt/placeholder-cover.jpg'} 
          alt={`Capa de ${company?.commercialName}`}
        />
      </Header>

      <Main>
        <ProfileSection>
          <ProfileHeader>
            <CompanyLogo 
              src={company?.logoBase64 || company?.logo || 'https://lognow.pt/placeholder-logo.png'} 
              alt={company?.commercialName}
            />
            <CompanyName>{company?.commercialName}</CompanyName>
            
            <VerifiedBadge>
              <FaShieldAlt />
              <span>Verificado pela LogNow</span>
            </VerifiedBadge>
          </ProfileHeader>

          {socialProfile?.bio && (
            <BioSection>
              <CompanyBio>{socialProfile.bio}</CompanyBio>
            </BioSection>
          )}

          <ContactInfo>
            {company?.commercialEmail && (
              <ContactItem>
                <FaEnvelope />
                <a href={`mailto:${company.commercialEmail}`}>{company.commercialEmail}</a>
              </ContactItem>
            )}
            {company?.commercialPhone && (
              <ContactItem>
                <FaPhone />
                <span>{company.commercialPhone}</span>
              </ContactItem>
            )}
            {company?.address && (
              <ContactItem>
                <FaMapMarkerAlt />
                <span>{company.address}</span>
              </ContactItem>
            )}
            {company?.workingHours && (
              <ContactItem>
                <FaClock />
                <span>{company.workingHours}</span>
              </ContactItem>
            )}
          </ContactInfo>
        </ProfileSection>

        <TrustSection>
          <TrustIndicators>
            <TrustItem>
              <TrustNumber>{reviews.length}</TrustNumber>
              <TrustLabel>Avaliações Verificadas</TrustLabel>
            </TrustItem>
            <TrustItem>
              <TrustNumber>{averageRating.toFixed(1)}</TrustNumber>
              <TrustLabel>Classificação Média</TrustLabel>
            </TrustItem>
            <TrustItem>
              <TrustNumber>
                <FaUserCheck size={24} />
              </TrustNumber>
              <TrustLabel>Vendedor Certificado</TrustLabel>
            </TrustItem>
          </TrustIndicators>
        </TrustSection>

        <RatingSection>
          <RatingOverview>
            <AverageRatingCard>
              <AverageRatingValue>{averageRating.toFixed(1)}</AverageRatingValue>
              <Stars>
                {[1, 2, 3, 4, 5].map((star) => (
                  star <= Math.round(averageRating) 
                    ? <StarFilled key={star}>★</StarFilled>
                    : <Star key={star}>★</Star>
                ))}
              </Stars>
              <RatingCount>
                {reviews.length} {reviews.length === 1 ? 'avaliação' : 'avaliações'}
              </RatingCount>
            </AverageRatingCard>

            <RatingDistribution>
              {[5, 4, 3, 2, 1].map((stars) => {
                const count = ratingDistribution[stars - 1];
                const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                
                return (
                  <RatingBar key={stars}>
                    <RatingStars>
                      {stars} <FaStar />
                    </RatingStars>
                    <RatingBarContainer>
                      <RatingBarFill style={{ width: `${percentage}%` }} />
                    </RatingBarContainer>
                    <RatingBarCount>{count}</RatingBarCount>
                  </RatingBar>
                );
              })}
            </RatingDistribution>
          </RatingOverview>
        </RatingSection>

        <ReviewsSection>
          <SectionTitle>Avaliações dos Clientes</SectionTitle>
          
          {reviews.length === 0 ? (
            <NoReviews>
              <p>Ainda não existem avaliações para este vendedor.</p>
            </NoReviews>
          ) : (
            <ReviewsList>
              {reviews.map((review) => (
                <ReviewCard key={review.id}>
                  <ReviewHeader>
                    <ReviewerInfo>
                      {review.userPhoto ? (
                        <ReviewerPhotoAvatar src={review.userPhoto} alt={review.userName || 'Cliente'} />
                      ) : (
                        <ReviewerAvatar>
                          {(review.userName || review.customerName) ? (review.userName || review.customerName).charAt(0).toUpperCase() : '?'}
                        </ReviewerAvatar>
                      )}
                      <CustomerName>{review.userName || review.customerName || 'Cliente'}</CustomerName>
                    </ReviewerInfo>
                    <ReviewDate>{formatDate(review.createdAt || review.date)}</ReviewDate>
                  </ReviewHeader>
                  
                  <ReviewRating>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <FaStar 
                        key={star} 
                        className={star <= review.rating ? 'filled' : ''}
                      />
                    ))}
                  </ReviewRating>
                  
                  <ReviewComment>{review.comment}</ReviewComment>
                  
                  <ReviewFooter>
                    {review.verified && (
                      <VerifiedPurchase>
                        <FaCheck />
                        <span>Compra verificada</span>
                      </VerifiedPurchase>
                    )}
                  </ReviewFooter>
                </ReviewCard>
              ))}
            </ReviewsList>
          )}
        </ReviewsSection>
      </Main>

      <Footer>
        <FooterContent>
          <p>Este serviço é prestado por:</p>
          <FooterLogoLink href="https://lognow.pt" target="_blank" rel="noopener noreferrer">
            <FooterLogo 
              src="https://lognow.pt/logo_lognow_preto.svg" 
              alt="LogNow" 
            />
          </FooterLogoLink>
          <FooterCopyright> LogNow. Todos os direitos reservados.</FooterCopyright>
        </FooterContent>
      </Footer>
    </Container>
  );
}
